'use client'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import type { UpdateUserProfileInput, UserProfile } from '@/types/user'
import { type ChangeEvent, useEffect, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { trpc } from '@/lib/trpc'
import { UpdateUserProfileInputSchema } from '@/types/user'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

interface ProfileInformationSettingsProps {
	onSuccess?: () => void
}

const MAX_FILE_SIZE = 2 * 1024 * 1024
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']

export function ProfileInformationSettings({ onSuccess }: ProfileInformationSettingsProps) {
	const { t } = useTranslation()
	const utils = trpc.useUtils()
	const { data: userProfile, isLoading: isLoadingProfile, error: profileError } = trpc.user.getUserProfile.useQuery()

	const [isUploading, setIsUploading] = useState(false)
	const fileInputRef = useRef<HTMLInputElement>(null)

	const profileForm = useForm<UpdateUserProfileInput>({
		resolver: zodResolver(UpdateUserProfileInputSchema),
		defaultValues: {
			name: '',
			avatarUrl: null,
		},
	})

	useEffect(() => {
		if (userProfile) {
			const profile = userProfile as UserProfile
			profileForm.reset({
				name: profile.name ?? '',
				avatarUrl: profile.avatarUrl,
			})
		}
	}, [userProfile, profileForm])

	const { mutate: updateUser, isPending: isUpdatingProfile } = trpc.user.updateUserProfile.useMutation({
		onSuccess: (updatedData) => {
			toast.success(t('profileSettings.updateSuccessToast'))
			utils.user.getUserProfile.invalidate()
			const profile = updatedData as UserProfile
			profileForm.reset({
				name: profile.name ?? '',
				avatarUrl: profile.avatarUrl,
			})
			setIsUploading(false)
			onSuccess?.()
		},
		onError: (error) => {
			toast.error(error.message || t('profileSettings.updateErrorToast'))
			if (userProfile) {
				const profile = userProfile as UserProfile
				profileForm.reset({
					name: profile.name ?? '',
					avatarUrl: profile.avatarUrl,
				})
			}
			setIsUploading(false)
		},
	})

	const { mutate: uploadAvatar } = trpc.upload.uploadAvatar.useMutation({
		onSuccess: (data) => {
			const currentFormValues = profileForm.getValues()
			updateUser({
				...currentFormValues,
				avatarUrl: data.publicUrl,
			})
		},
		onError: (error) => {
			toast.error(error.message || t('profileSettings.uploadErrorToast'))
			setIsUploading(false)
		},
	})

	const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0]
		if (!file) {
			return
		}

		if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
			toast.error(t('profileSettings.invalidFileTypeToast'))
			return
		}

		if (file.size > MAX_FILE_SIZE) {
			toast.error(t('profileSettings.fileTooLargeToast'))
			return
		}

		setIsUploading(true)

		const reader = new FileReader()
		reader.onloadend = () => {
			const base64String = reader.result as string
			uploadAvatar({ fileAsBase64: base64String, fileType: file.type })
		}
		reader.onerror = (error) => {
			toast.error(t('profileSettings.fileReadErrorToast'))
			setIsUploading(false)
		}
		reader.readAsDataURL(file)

		if (fileInputRef.current) {
			fileInputRef.current.value = ''
		}
	}

	const handleRemoveAvatar = () => {
		const currentFormValues = profileForm.getValues()
		updateUser({
			...currentFormValues,
			avatarUrl: null,
		})
	}

	function onProfileSubmit(values: UpdateUserProfileInput) {
		if (userProfile && values.name !== (userProfile as UserProfile).name) {
			updateUser({
				name: values.name,
				avatarUrl: profileForm.getValues('avatarUrl'),
			})
		} else if (userProfile) {
			toast.info(t('profileSettings.noChangesToast'))
		} else {
			toast.error(t('profileSettings.dataNotLoadedToast'))
		}
	}

	if (isLoadingProfile) {
		return (
			<div className='space-y-6 p-1'>
				<div className='flex items-center space-x-4'>
					<Skeleton className='h-16 w-16 rounded-full' />
					<div className='space-y-2'>
						<Skeleton className='h-10 w-32' />
						<Skeleton className='h-10 w-32' />
					</div>
				</div>
				<div className='space-y-2'>
					<Skeleton className='h-4 w-20' />
					<Skeleton className='h-10 w-full' />
				</div>
				<div className='space-y-2'>
					<Skeleton className='h-4 w-20' />
					<Skeleton className='h-10 w-full' />
				</div>
				<Skeleton className='h-10 w-32' />
			</div>
		)
	}

	if (profileError) {
		return (
			<div className='p-1 text-destructive'>
				{t('profileSettings.loadErrorToast', { error: profileError.message })}
			</div>
		)
	}

	if (!userProfile) {
		return <div className='p-1 text-muted-foreground'>{t('profileSettings.loadErrorNoData')}</div>
	}

	const profile = userProfile as UserProfile
	const currentAvatarUrl = profileForm.watch('avatarUrl')

	return (
		<Form {...profileForm}>
			<form
				onSubmit={profileForm.handleSubmit(onProfileSubmit)}
				className='space-y-4 p-1' // Use space-y-4 (16px)
			>
				<FormItem>
					<FormLabel>{t('profileSettings.avatarLabel')}</FormLabel>
					<div className='flex items-center space-x-4 pt-2'>
						<Avatar className='h-16 w-16'>
							<AvatarImage src={currentAvatarUrl ?? undefined} alt={profile.name ?? 'User'} />
							<AvatarFallback>
								{profile.name
									? profile.name
											.split(' ')
											.map((n) => n[0])
											.slice(0, 2)
											.join('')
											.toUpperCase()
									: profile.email[0]?.toUpperCase() ?? '?'}
							</AvatarFallback>
						</Avatar>
						<div className='flex flex-col space-y-2'>
							<Button
								type='button'
								variant='outline'
								size='sm'
								onClick={() => fileInputRef.current?.click()}
								disabled={isUploading || isUpdatingProfile}
							>
								{isUploading ? t('common.uploading') : t('profileSettings.changeAvatarButton')}
							</Button>
							{currentAvatarUrl && (
								<Button
									type='button'
									variant='ghost'
									size='sm'
									className='text-destructive hover:text-destructive'
									onClick={handleRemoveAvatar}
									disabled={isUploading || isUpdatingProfile}
								>
									{t('profileSettings.removeAvatarButton')}
								</Button>
							)}
						</div>

						<FormControl>
							<Input
								type='file'
								ref={fileInputRef}
								className='hidden'
								accept={ACCEPTED_IMAGE_TYPES.join(',')}
								onChange={handleFileChange}
								disabled={isUploading || isUpdatingProfile}
							/>
						</FormControl>
					</div>
					<FormMessage />
				</FormItem>

				<FormItem>
					<FormLabel>{t('common.email')}</FormLabel>
					<FormControl>
						<div className='flex items-center gap-2'>
							<Input value={profile.email} readOnly disabled className='bg-muted/50 flex-1' />
							<Badge variant={profile.emailVerified ? 'outline' : 'secondary'}>
								{profile.emailVerified
									? t('profileSettings.emailVerified')
									: t('profileSettings.emailNotVerified')}
							</Badge>
						</div>
					</FormControl>
				</FormItem>

				<FormItem>
					<FormLabel>{t('profileSettings.usernameLabel')}</FormLabel>
					<FormControl>
						<Input
							value={profile.username ?? t('profileSettings.usernameNotAvailable')}
							readOnly
							disabled
							className='bg-muted/50'
						/>
					</FormControl>
				</FormItem>

				<FormField
					control={profileForm.control}
					name='name'
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('common.name')}</FormLabel>
							<FormControl>
								<Input
									placeholder={t('registerForm.namePlaceholder')}
									{...field}
									value={field.value ?? ''}
									disabled={isUploading || isUpdatingProfile}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<Button
					type='submit'
					disabled={isUploading || isUpdatingProfile || !profileForm.formState.isDirty}
					// Removed mt-4, rely on parent space-y-4
				>
					{isUpdatingProfile ? t('common.saving') : t('profileSettings.saveButton')}
				</Button>
			</form>
		</Form>
	)
}
